import "./DestinationDetail.css";

import { Controller } from "@hotwired/stimulus";
import {
    buildOpenWeatherUrl,
    formatWeatherTemperature,
    getInitialDestinationTab,
    HorizontalRailScroller,
    renderDestinationWeatherIcon,
    setActiveDestinationTab,
    setDestinationHeaderSolid,
    setDestinationLanguageMenuOpen,
} from "./DestinationDetailUi";

type OpenWeatherForecast = {
    list?: {
        main?: {
            temp?: number
        },
        weather?: {
            icon?: string
        }[]
    }[]
};

export default class DestinationDetailController extends Controller {
    static targets = ["tab", "panel", "headerTitle", "weather"];

    declare readonly tabTargets: HTMLElement[];
    declare readonly weatherTarget: HTMLElement;
    declare readonly hasWeatherTarget: boolean;

    private isConnectedToPage = false;
    private horizontalRailScroller?: HorizontalRailScroller;
    private readonly onHashChange = (): void => this.selectTabFromHash();
    private readonly onLanguageDialogKeyDown = (event: KeyboardEvent): void => {
        const menu = this.element.querySelector<HTMLElement>("[data-destination-language-menu]");
        if (event.key !== "Escape" || !menu || menu.hidden) return;

        event.stopPropagation();
        this.closeLanguageMenu(event);
    };
    private readonly onLanguageDialogOutsideClick = (event: MouseEvent): void => {
        const menu = this.element.querySelector<HTMLElement>("[data-destination-language-menu]");
        if (!menu || menu.hidden || !(event.target instanceof Node) || menu.contains(event.target)) return;

        event.stopPropagation();
        this.closeLanguageMenu(event);
    };

    connect(): void {
        this.isConnectedToPage = true;
        this.selectTabFromHash();
        window.addEventListener("hashchange", this.onHashChange);
        document.addEventListener("keydown", this.onLanguageDialogKeyDown);
        document.addEventListener("click", this.onLanguageDialogOutsideClick, true);

        this.horizontalRailScroller = new HorizontalRailScroller({
            root: this.element as HTMLElement,
            railSelector: ".destination-thumbnail-rail, .destination-card-list, .destination-explore-rail",
            scrollLeftDataAttribute: "destinationScrollLeft"
        });
        this.horizontalRailScroller.connect();
        window.addEventListener("scroll", this.updateSolidHeader, { passive: true });
        this.updateSolidHeader();
        void this.loadWeather();
    }

    private selectTabFromHash(): void {
        const renderedTabs = this.tabTargets
            .map(tab => tab.dataset.tabName)
            .filter((name): name is string => !!name);

        if (renderedTabs.length > 0) {
            const initialTab = getInitialDestinationTab(window.location.hash, renderedTabs);
            if (initialTab) {
                setActiveDestinationTab(this.element, initialTab);
                this.removeRedundantDefaultTabHash(initialTab, renderedTabs[0]);
            }
        }

    }

    disconnect(): void {
        this.isConnectedToPage = false;
        window.removeEventListener("hashchange", this.onHashChange);
        document.removeEventListener("keydown", this.onLanguageDialogKeyDown);
        document.removeEventListener("click", this.onLanguageDialogOutsideClick, true);
        window.removeEventListener("scroll", this.updateSolidHeader);
        this.horizontalRailScroller?.disconnect();
        this.horizontalRailScroller = undefined;
        const button = this.element.querySelector<HTMLElement>(".destination-language-button");
        if (button) {
            setDestinationLanguageMenuOpen(this.element, button, false);
        }
    }

    selectTab(event: Event): void {
        const mouse = event as MouseEvent;
        if (mouse.ctrlKey || mouse.metaKey || mouse.shiftKey || mouse.altKey ||
            (typeof mouse.button === "number" && mouse.button !== 0)) return;
        const tab = event.currentTarget as HTMLElement;
        const tabName = tab.dataset.tabName;
        if (!tabName) {
            return;
        }

        if (this.shouldNavigateHomeFromBottomExplore(tab, tabName)) {
            event.preventDefault();
            this.visitHome();
            return;
        }

        if (setActiveDestinationTab(this.element, tabName)) {
            event.preventDefault();
            this.replaceTabHistory(tabName);
        }
    }

    private shouldNavigateHomeFromBottomExplore(tab: HTMLElement, tabName: string): boolean {
        return tab.dataset.smartguideBottomNavigationKey === "explore" &&
            this.tabTargets.some(item =>
                item.dataset.tabName === tabName &&
                item.getAttribute("aria-selected") === "true"
            );
    }

    private visitHome(): void {
        const turbo = (window as Window & { Turbo?: { visit: (url: string) => void } }).Turbo;
        if (turbo) {
            turbo.visit("/");
            return;
        }

        window.location.assign("/");
    }

    private replaceTabHistory(tabName: string): void {
        const defaultTabName = this.tabTargets[0]?.dataset.tabName;
        history.replaceState(
            history.state,
            "",
            tabName === defaultTabName
                ? `${window.location.pathname}${window.location.search}`
                : `#${tabName}`
        );
    }

    private removeRedundantDefaultTabHash(initialTab: string, defaultTabName: string | undefined): void {
        if (!defaultTabName || initialTab !== defaultTabName || window.location.hash !== `#${defaultTabName}`) {
            return;
        }

        history.replaceState(history.state, "", `${window.location.pathname}${window.location.search}`);
    }

    toggleLanguageMenu(event: Event): void {
        event.preventDefault();
        const button = event.currentTarget as HTMLElement;
        const open = button.getAttribute("aria-expanded") !== "true";
        setDestinationLanguageMenuOpen(this.element, button, open);
    }

    closeLanguageMenu(event: Event): void {
        event.preventDefault();
        const button = this.element.querySelector<HTMLElement>(".destination-language-button");
        if (!button) {
            return;
        }

        setDestinationLanguageMenuOpen(this.element, button, false);
        button.focus({ preventScroll: true });
    }

    private readonly updateSolidHeader = (): void => {
        const hero = this.element.querySelector<HTMLElement>("[data-destination-hero]");
        const header = this.element.querySelector<HTMLElement>("[data-destination-top-header]");
        if (!hero || !header) {
            return;
        }

        setDestinationHeaderSolid(this.element, window.scrollY > hero.offsetHeight - header.offsetHeight);
    };

    private async loadWeather(): Promise<void> {
        if (!this.hasWeatherTarget) {
            return;
        }

        const latitude = Number(this.weatherTarget.dataset.weatherLatitude);
        const longitude = Number(this.weatherTarget.dataset.weatherLongitude);
        const language = this.weatherTarget.dataset.weatherLanguage || "en";
        const units = this.weatherTarget.dataset.weatherUnits || "metric";
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            return;
        }

        try {
            const response = await fetch(buildOpenWeatherUrl(latitude, longitude, language, units));
            if (!response.ok) {
                return;
            }

            const payload = await response.json() as OpenWeatherForecast;
            const today = payload.list?.[0];
            if (!today) {
                return;
            }

            const temperature = today.main?.temp;
            if (typeof temperature !== "number") {
                return;
            }

            if (!this.isConnectedToPage) {
                return;
            }

            const temperatureTarget = this.weatherTarget.querySelector<HTMLElement>("[data-weather-temperature]");
            const iconTarget = this.weatherTarget.querySelector<HTMLElement>("[data-weather-icon]");
            if (!temperatureTarget || !iconTarget) {
                return;
            }

            temperatureTarget.textContent = formatWeatherTemperature(temperature);
            iconTarget.innerHTML = renderDestinationWeatherIcon(today.weather?.[0]?.icon);
            this.weatherTarget.hidden = false;
        } catch {
            this.weatherTarget.hidden = true;
        }
    }
}
